import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	FormGroupComponent,
	ITab,
	LinkComponent,
	ModalComponent,
	TabsComponent,
	TextareaComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { Subject, takeUntil } from 'rxjs';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { WORKFLOW_FORM_I18N_KEYS } from '../../constants/workflow-form-defaults.constants';
import { ITextLocaleMap } from '../../models/workflows.models';
import { WorkflowFormDefaultsService } from '../../services/workflow-form-defaults.service';
import {
	clearSiblingLocalesStillAtDefault,
	hasWorkflowLocaleText,
	isWorkflowLocaleMapCustomized,
	stripWorkflowLocaleMapIfStillDefaults,
} from '../../utils/workflow-locale-text.util';

type FinishMessageSection = 'loading' | 'ok' | 'ko' | 'cancelled' | 'pending';

/** Campos del modal: vacío/defaults = Identity; personalizado → obligatorio en todos. */
const MESSAGE_FIELDS = [
	'loading_text',
	'ok_title',
	'ok_description',
	'ko_title',
	'ko_description',
	'cancelled_title',
	'cancelled_description',
	'pending_title',
	'pending_description',
] as const;
type FinishMessageField = (typeof MESSAGE_FIELDS)[number];

/** Clave i18n de default Tecalis por campo del modal (NIF-762). */
const FIELD_DEFAULT_I18N_KEY: Record<FinishMessageField, string> = {
	loading_text: WORKFLOW_FORM_I18N_KEYS.FINISH_LOADING_TEXT,
	ok_title: WORKFLOW_FORM_I18N_KEYS.FINISH_OK_TITLE,
	ok_description: WORKFLOW_FORM_I18N_KEYS.FINISH_OK_DESCRIPTION,
	ko_title: WORKFLOW_FORM_I18N_KEYS.FINISH_KO_TITLE,
	ko_description: WORKFLOW_FORM_I18N_KEYS.FINISH_KO_DESCRIPTION,
	cancelled_title: WORKFLOW_FORM_I18N_KEYS.FINISH_CANCELLED_TITLE,
	cancelled_description: WORKFLOW_FORM_I18N_KEYS.FINISH_CANCELLED_DESCRIPTION,
	pending_title: WORKFLOW_FORM_I18N_KEYS.FINISH_PENDING_TITLE,
	pending_description: WORKFLOW_FORM_I18N_KEYS.FINISH_PENDING_DESCRIPTION,
};

/**
 * Modal «Mensajes de resultado» (NIF-762).
 * Borrador local: Guardar emite el FormGroup; Cancelar emite null.
 * Prefill defaults; al editar un idioma limpia siblings aún en default (comentario 124138).
 * UI: Infinity Modal scrollable + Alert message/warning + Tabs pills + Tile + Textarea.
 */
@Component({
	selector: 'app-modal-finish-result-messages',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		ModalComponent,
		TabsComponent,
		TextareaComponent,
		FormGroupComponent,
		AlertComponent,
		TileComponent,
		LinkComponent,
	],
	templateUrl: './modal-finish-result-messages.component.html',
	styleUrl: './modal-finish-result-messages.component.scss',
	/** Necesario: overlay del modal se mueve a `body`; `:has(.finish-result-messages)` acota el max-height. */
	encapsulation: ViewEncapsulation.None,
})
export class ModalFinishResultMessagesComponent implements OnInit, OnDestroy {
	private readonly trans = inject(TranslateService);
	private readonly workflowFormDefaults = inject(WorkflowFormDefaultsService);
	readonly reactiveFormService = inject(ReactiveFormsService);

	@Input({ required: true }) form!: FormGroup;
	@Input() localeTabs: ITab[] = [];
	@Output() closeEvent = new EventEmitter<{ form: FormGroup | null; show: boolean }>();

	selectedLocaleId = 'es';
	tabs: ITab[] = [];
	private saveAttempted = false;
	private readonly destroy$ = new Subject<void>();
	private readonly defaultsByField = new Map<FinishMessageField, ITextLocaleMap>();
	/** Evita bucles al limpiar siblings con setValue. */
	private clearingSiblings = false;

	ngOnInit(): void {
		this.selectedLocaleId = String(this.localeTabs.find((t) => t.selected)?.id ?? this.localeTabs[0]?.id ?? 'es');
		this.cacheFieldDefaults();
		this.seedEmptyFieldsWithDefaults();
		this.subscribeClearSiblingDefaults();
		this.refreshTabs();
		this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshTabs());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onLocaleTabSelected(tab: ITab): void {
		const id = tab?.id != null ? String(tab.id) : '';
		if (!id) {
			return;
		}
		this.selectedLocaleId = id;
		this.refreshTabs();
	}

	localeControl(field: string): FormControl {
		return this.form.get(`${field}.${this.selectedLocaleId}`) as FormControl;
	}

	/** Error visual: required Angular o regla all-or-nothing tras intento de guardar. */
	fieldHasError(field: FinishMessageField): boolean {
		const control = this.localeControl(field);
		if (this.reactiveFormService.hasError(control)) {
			return true;
		}
		return this.saveAttempted && this.fieldIncompleteForLocale(field, this.selectedLocaleId);
	}

	resetSection(section: FinishMessageSection): void {
		const locale = this.selectedLocaleId;
		const restore = (field: FinishMessageField): void => {
			const defaults = this.defaultsFor(field);
			this.form.get(`${field}.${locale}`)?.setValue(String(defaults[locale] ?? ''), { emitEvent: false });
		};

		switch (section) {
			case 'loading':
				restore('loading_text');
				break;
			case 'ok':
				restore('ok_title');
				restore('ok_description');
				break;
			case 'ko':
				restore('ko_title');
				restore('ko_description');
				break;
			case 'cancelled':
				restore('cancelled_title');
				restore('cancelled_description');
				break;
			case 'pending':
				restore('pending_title');
				restore('pending_description');
				break;
		}
		this.refreshTabs();
	}

	closeModal(type: string): void {
		if (type === 'buttonPrimary') {
			const incomplete = this.firstIncompleteLocale();
			if (this.form.invalid || incomplete) {
				this.saveAttempted = true;
				this.form.markAllAsTouched();
				if (incomplete) {
					this.selectedLocaleId = incomplete;
				}
				this.refreshTabs();
				return;
			}
			this.stripUntouchedDefaultsBeforeEmit();
			this.closeEvent.emit({ form: this.form, show: false });
			return;
		}
		this.closeEvent.emit({ form: null, show: false });
	}

	private cacheFieldDefaults(): void {
		for (const field of MESSAGE_FIELDS) {
			this.defaultsByField.set(field, this.workflowFormDefaults.getLocaleMapForKey(FIELD_DEFAULT_I18N_KEY[field]));
		}
	}

	private defaultsFor(field: FinishMessageField): ITextLocaleMap {
		return this.defaultsByField.get(field) ?? {};
	}

	/** Prefill solo campos sin texto (API vacía → copies Tecalis). */
	private seedEmptyFieldsWithDefaults(): void {
		const locales = this.allowedLocales();
		for (const field of MESSAGE_FIELDS) {
			const group = this.form.get(field) as FormGroup | null;
			if (!group) {
				continue;
			}
			const raw = group.getRawValue() as ITextLocaleMap;
			if (hasWorkflowLocaleText(raw)) {
				continue;
			}
			const defaults = this.defaultsFor(field);
			const patch = Object.fromEntries(locales.map((l) => [l, String(defaults[l] ?? '')]));
			group.patchValue(patch, { emitEvent: false });
		}
	}

	/** Al salir del default en un idioma, limpia siblings aún en default (comentario 124138). */
	private subscribeClearSiblingDefaults(): void {
		const locales = this.allowedLocales();
		for (const field of MESSAGE_FIELDS) {
			const group = this.form.get(field) as FormGroup | null;
			if (!group) {
				continue;
			}
			for (const locale of locales) {
				const control = group.get(locale);
				if (!control) {
					continue;
				}
				control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
					if (this.clearingSiblings) {
						return;
					}
					this.applyClearSiblingsForField(field, locale);
				});
			}
		}
	}

	private applyClearSiblingsForField(field: FinishMessageField, changedLocale: string): void {
		const group = this.form.get(field) as FormGroup | null;
		if (!group) {
			return;
		}
		const locales = this.allowedLocales();
		const map = group.getRawValue() as ITextLocaleMap;
		const cleared = clearSiblingLocalesStillAtDefault(map, changedLocale, this.defaultsFor(field), locales);
		const changed = locales.some((l) => String(cleared[l] ?? '') !== String(map[l] ?? ''));
		if (!changed) {
			return;
		}
		this.clearingSiblings = true;
		try {
			for (const locale of locales) {
				if (locale === changedLocale) {
					continue;
				}
				if (String(cleared[locale] ?? '') !== String(map[locale] ?? '')) {
					group.get(locale)?.setValue(cleared[locale] ?? '', { emitEvent: false });
				}
			}
		} finally {
			this.clearingSiblings = false;
		}
		this.refreshTabs();
	}

	/** No persistir copies Tecalis si el usuario no personalizó el campo. */
	private stripUntouchedDefaultsBeforeEmit(): void {
		const locales = this.allowedLocales();
		for (const field of MESSAGE_FIELDS) {
			const group = this.form.get(field) as FormGroup | null;
			if (!group) {
				continue;
			}
			const stripped = stripWorkflowLocaleMapIfStillDefaults(
				group.getRawValue() as ITextLocaleMap,
				this.defaultsFor(field),
				locales,
			);
			group.patchValue(stripped, { emitEvent: false });
		}
	}

	private refreshTabs(): void {
		this.tabs = (this.localeTabs.length ? this.localeTabs : [{ id: 'es', label: 'ES' }]).map((tab) => {
			const id = String(tab.id);
			return {
				...tab,
				id,
				label: tab.label || this.trans.instant(`MASTER.LANGUAGE.${id.toUpperCase()}`),
				selected: id === this.selectedLocaleId,
				alert: this.localeIsIncomplete(id),
			};
		});
	}

	/** Locale incompleto: algún campo personalizado con hueco en ese idioma. */
	private localeIsIncomplete(locale: string): boolean {
		return MESSAGE_FIELDS.some((field) => this.fieldIncompleteForLocale(field, locale));
	}

	private firstIncompleteLocale(): string | null {
		for (const tab of this.tabs) {
			const id = String(tab.id);
			if (this.localeIsIncomplete(id)) {
				return id;
			}
		}
		return null;
	}

	/**
	 * Todo vacío o todo = defaults → OK (Identity/PWCS).
	 * Si el campo está personalizado → todos los locales deben tener valor.
	 */
	private fieldIncompleteForLocale(field: FinishMessageField, locale: string): boolean {
		const locales = this.allowedLocales();
		const map = Object.fromEntries(locales.map((l) => [l, this.fieldValue(field, l)])) as ITextLocaleMap;
		if (!isWorkflowLocaleMapCustomized(map, this.defaultsFor(field), locales)) {
			return false;
		}
		return this.fieldValue(field, locale) === '';
	}

	private fieldValue(field: FinishMessageField, locale: string): string {
		return String(this.form.get(`${field}.${locale}`)?.value ?? '').trim();
	}

	private allowedLocales(): string[] {
		const fromTabs = (this.localeTabs.length ? this.localeTabs : this.tabs)
			.map((t) => String(t.id))
			.filter(Boolean);
		if (fromTabs.length) {
			return fromTabs;
		}
		return ['es'];
	}
}
